import { Component, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Point } from 'app/shared/ll/models/geometry';
import { UiModule } from '../ui';
import { GoogleStreetViewImageService } from './google-street-view-image.service';


type Address = {
    addressShort: string;
    municipality: string;
    postalCode: string | null;
    regionShort: string;
    countryLong: string;
};


@Component({
    selector: 'google-street-view-image',
    imports: [
        UiModule,
    ],
    styles: [
        `
        @media screen, print {
            :host {
                display: block;
                position: relative;
            }
            .wrapper {
                height: 100%;
            }
            .street-view-img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
            .show-street-view {
                cursor: pointer;
            }
            .click-to-show {
                position: absolute;
                top: 0;
                width: 100%;
                left: 0;
                cursor: pointer;
                padding: 0.5rem;
            }
        }
        `,
    ],
    template: `
        @if (showImage()) {
            <div class="wrapper">
                @if (isLoading()) {
                    <ui-block-loading></ui-block-loading>
                }
                <img
                    alt="street view image"
                    class="street-view-img"
                    [hidden]="isLoading()"
                    [src]="url()"
                    [width]="width()"
                    [height]="height()"
                    [class.rounded-circle]="shape() == 'round'"
                    (load)="onImageLoaded()"
                    (error)="onImageLoaded()">
            </div>
        }
        @else {
            <div class="wrapper" tooltip="Click to show Street View" (click)="showStreetView()">
                <img alt="no image available"
                    class="street-view-img show-street-view"
                    [src]="noImageUrl"
                    [width]="width()"
                    [height]="height()">
                <div class="click-to-show">
                    Click to show <br> Street View
                </div>
            </div>
        }
    `,
})
export class GoogleStreetViewImageComponent {

    width = input.required<number>();

    height = input.required<number>();

    address = input.required<Address>();

    center = input.required<Point>();

    shape = input<'round' | 'rectangle'>('rectangle');

    clickToShow = input(true);

    url = model('');

    urlChanges = output<string>();

    showImage = signal(false);

    noImageUrl = 'assets/images/no-image.svg';

    isLoading = signal(true);

    private googleStreetViewImageService = inject(GoogleStreetViewImageService);

    private destroyRef = inject(DestroyRef);

    constructor() {
        effect(() => {
            const url = this.url();
            const clickToShow = this.clickToShow();
            this.showImage.set(!!url);
            if (!url && !clickToShow) {
                this.showStreetView();
            }
        });
    }

    onImageLoaded() {
        this.isLoading.set(false);
    }

    showStreetView() {
        this.showImage.set(true);
        this.url.set('');
        const address = [
            this.address().addressShort,
            this.address().municipality,
            this.address().postalCode,
            this.address().regionShort,
            this.address().countryLong,
        ].filter(x => x).join(' ');

        this.googleStreetViewImageService.getImageUrl(this.center(), address, this.width(), this.height()).pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: url => {
                this.showImage.set(true);
                this.url.set(url || this.noImageUrl);
                this.urlChanges.emit(this.url());
            },
            error: () => {},
        });
    }
}
